import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Box,
  Typography,
  Link,
  CircularProgress,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Button, ButtonVariantOption } from "@/components/Button";
import { useApiClient } from "@/hooks/useApiClient";
import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import { FileUploadButton } from "@/features/realEstateReceptionBookFeed/components/FileUploadButton";
import { readString } from "react-papaparse";
import { type ParseResult } from "papaparse";
import { Label } from "@/components/Label";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  modalTitle: string;
  sampleFileName: string;
  confirmButtonLabel: string;
  apiEndpoint: string;
  onUploadSuccess: () => void;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const MAX_CSV_ROWS = 1_000;

// Zodスキーマ定義
const FormDataSchema = z.object({
  name: z
    .string()
    .min(1, "識別名は必須です")
    .max(255, "識別名は255文字以内で入力してください"),
});

// 定義したZodのスキーマをTypescriptの型に変換
type FormData = z.infer<typeof FormDataSchema>;

/**
 * CSVファイルの読み込み処理
 */
const readCSV = async (file: File): Promise<ParseResult<unknown>> =>
  await new Promise<ParseResult<string>>((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("error", () => {
      reject(reader.error);
    });
    reader.addEventListener("load", () => {
      const { result } = reader;
      if (typeof result !== "string") {
        reject(reader.error);
      }
      readString<string>(result as string, {
        worker: true,
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          resolve(results);
        },
        error(error: Error) {
          reject(error);
        },
      });
    });
    reader.readAsText(file);
  });

export const MapPlotRequestCsvUploadModal: React.FC<Props> = ({
  open,
  setOpen,
  modalTitle,
  sampleFileName,
  confirmButtonLabel,
  apiEndpoint,
  onUploadSuccess,
  loading,
  setLoading,
}) => {
  const [fileToCsvUpload, setFileToCsvUpload] = useState<File | null>(null);
  const [showFileErrorMessage, setShowFileErrorMessage] =
    useState<boolean>(false);
  const { apiClient } = useApiClient();

  // React Hook Form の初期化
  const { control, handleSubmit, reset, watch } = useForm<FormData>({
    mode: "all",
    defaultValues: {
      name: "",
    },
    resolver: zodResolver(FormDataSchema),
  });

  // 識別名の値を監視
  const nameValue = watch("name");

  // 保存ボタンの有効状態
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);

  // nameValueとfileToCsvUploadの状態が変わったら保存ボタンの有効状態を更新
  useEffect(() => {
    setIsButtonDisabled(!fileToCsvUpload || loading || !nameValue.trim());
  }, [fileToCsvUpload, loading, nameValue]);

  const handleClose = (): void => {
    setOpen(false);
    setFileToCsvUpload(null);
    setShowFileErrorMessage(false);
    reset(); // フォームの値をリセット
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    setShowFileErrorMessage(false);
    const files = event.target.files;
    if (files && files.length > 0) {
      try {
        const csv = await readCSV(files[0]);
        if (csv.data.length > MAX_CSV_ROWS) {
          setShowFileErrorMessage(true);
          setFileToCsvUpload(null);
          return;
        }
        setFileToCsvUpload(files[0]);
      } catch (error) {
        console.error(error);
        toast.error("CSVファイルの読み込みに失敗しました");
        setFileToCsvUpload(null);
      }
    }
  };

  const handleFileChangeWrapper = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    void handleFileChange(event);
  };

  const onSubmit = (data: FormData): void => {
    if (!fileToCsvUpload) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("file", fileToCsvUpload);
    formData.append("name", data.name);

    apiClient
      .post(apiEndpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        onUploadSuccess();
        setLoading(false);
        handleClose();
      })
      .catch((error: Error) => {
        setLoading(false);
        console.error(error);
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.BadRequest
        ) {
          let errorMessage = "ファイル内容をご確認ください。";

          try {
            const responseData = error.response?.data as { file?: unknown };
            if (
              responseData?.file &&
              Array.isArray(responseData.file) &&
              responseData.file.length > 0 &&
              typeof responseData.file[0] === "string"
            ) {
              errorMessage = responseData.file[0];
            }
          } catch (e) {
            console.error("エラーメッセージの解析に失敗しました", e);
          }

          toast.error(
            <div>
              CSVファイルのアップロードに失敗しました。
              <br />
              {errorMessage}
            </div>
          );
        } else {
          toast.error("CSVファイルのアップロードに失敗しました");
        }
      });
  };

  // ボタンクリック時のハンドラー
  const handleButtonClick = (): void => {
    void handleSubmit(onSubmit)();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth={"md"}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{modalTitle}</DialogTitle>
        <DialogContent>
          <Link href={`/assets/${sampleFileName}`}>見本CSVをダウンロード</Link>
          <Stack alignItems="center">
            <Box>
              <Typography variant="h6">CSVをアップロードする</Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                mt: 2,
                width: "100%",
                maxWidth: "300px",
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Label required={true}>識別名</Label>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="識別名"
                      variant="outlined"
                      inputProps={{ maxLength: 255 }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Box>
              <FileUploadButton
                variant={ButtonVariantOption.Contained}
                onChange={handleFileChangeWrapper}
                accept={".csv"}
                label={"アップロード"}
              />
              {fileToCsvUpload && (
                <Typography>{fileToCsvUpload.name}</Typography>
              )}
              {showFileErrorMessage && (
                <Typography color="error">
                  {`CSVファイルの行数は${MAX_CSV_ROWS.toLocaleString()}以下にしてください。`}
                </Typography>
              )}
            </Box>
          </Stack>

          <Box sx={{ mt: 3, width: "100%" }}>
            <Typography variant="caption" color="text.secondary">
              ※ プロット可能なデータは最大で1,000件までです
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.5 }}
            >
              ※
              データの登録はバックグラウンドで処理されます。処理完了後にメールでお知らせが届きましたら、データを取得できます
            </Typography>
          </Box>
        </DialogContent>
        <Box sx={{ mx: "auto", my: 4 }}>
          <Button
            variant={ButtonVariantOption.Contained}
            onClick={handleButtonClick}
            disabled={isButtonDisabled}
            label={confirmButtonLabel}
          />
        </Box>
      </Dialog>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </>
  );
};
